/* =====================================================================================
   What we still do not know about a site, and the one-click way to fix it.

   Capture already existed and the structured fields were still empty: somebody logs "they send
   chimerism to the regional lab" as free-text intelligence, atlas.sites.incumbent_vendors stays
   null, the search cannot use it, and the next person asks the same question. Collecting cost
   something and leaving a blank cost nothing.

   Average completeness across 1,433 sites when this was written: 5 per cent, and NOT ONE site
   where we knew who supplies them today. That number is the argument for this file.
   ===================================================================================== */

import { BACKEND } from './db';
import { dbSiteId } from './ids';

export interface Completeness { completeness: number; missing: string[] }

export async function fetchCompleteness(seedSiteId: string): Promise<Completeness | null> {
  if (BACKEND !== 'supabase') return null;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('atlas').from('v_site_completeness')
    .select('completeness, missing').eq('site_id', dbSiteId(seedSiteId)).maybeSingle();
  if (error || !data) return null;
  return {
    completeness: Number((data as Record<string, unknown>).completeness ?? 0),
    missing: Array.isArray((data as Record<string, unknown>).missing)
      ? ((data as Record<string, unknown>).missing as unknown[]).map(String) : [],
  };
}

/* The single most commercially useful fact in the whole profile, and the one that was blank for
   every site in the model. Written to the structured column rather than to a note, because a
   note cannot be searched and the whole point is to find the centres somebody is already
   selling to. */
export async function setIncumbent(seedSiteId: string, vendors: string[]): Promise<void> {
  if (BACKEND !== 'supabase') throw new Error('This needs the database.');
  const { supabase } = await import('./supabase');
  const clean = vendors.map((v) => v.trim()).filter(Boolean);
  const { error } = await supabase().schema('atlas').from('sites')
    .update({ incumbent_vendors: clean }).eq('id', dbSiteId(seedSiteId));
  if (error) throw new Error(error.message);
}

export async function setSendOuts(seedSiteId: string, tests: string[]): Promise<void> {
  if (BACKEND !== 'supabase') throw new Error('This needs the database.');
  const { supabase } = await import('./supabase');
  const clean = tests.map((v) => v.trim()).filter(Boolean);
  /* Upsert on site_id: the technical record may not exist yet, and requiring somebody to fill a
     fifty-field form before they can record one fact is how the fifty-field form stays empty. */
  const { error } = await supabase().schema('atlas').from('site_technical')
    .upsert({ site_id: dbSiteId(seedSiteId), send_out_tests: clean,
              source: 'Field', confidence: 'verified', observed_on: new Date().toISOString().slice(0, 10) },
            { onConflict: 'site_id' });
  if (error) throw new Error(error.message);
}
